<template>
  <portal selector="#modal-portal">
    <div
      v-if="isVisible"
      @click="closeModal"
      :class="[{ '--full-height': fullHeight }, 'p-fixed v-modal__backdrop']"
      :style="{ zIndex: 150 + zIndex }"
    ></div>
    <transition :name="transitionModalName">
      <div
        v-if="isOpen"
        :class="[
          {
            '--short': short,
            '--x-short': xShort,
            '--video-fullscreen': isFullscreen,
            '--w-overlay': !!$slots.overlay,
            '--vertical': vertical,
            '--full-height': fullHeight,
            '--overflow-hidden': overflowHidden,
            '--fixed-right': right,
            '--hiding': persistent ? isHiding : false,
            '--hidden': persistent ? isHidden : false
          },
          modalClass,
          'p-fixed v-modal'
        ]"
        data-role="modal"
        data-cy="modal"
        @click.self="closeModal"
        :style="{ zIndex: 150 + zIndex + 1 }"
      >
        <div class="p-relative v-modal__content">
          <div
            v-if="!disableClose"
            data-cy="close-modal"
            class="p-absolute v-modal__close"
          >
            <v-button round outlined secondary @click="closeModal">
              <v-icon size="24px" icon="close" />
            </v-button>
          </div>

          <div
            v-if="$slots.overlay"
            class="v-modal__overlay container"
            :style="{
              '--app-height': appHeightStyle,
              [APP_HEIGHT_INITIAL_CSS_VAR]: appHeightStyle
            }"
            data-cy="modal-overlay"
          >
            <slot name="overlay"></slot>
          </div>

          <div
            class="v-modal__content-wrapper"
            data-role="modal-content-wrapper"
          >
            <div class="v-modal__before container">
              <slot name="before" />
            </div>

            <transition name="fade-out-left" mode="out-in">
              <div :key="activeHash" class="v-modal__page container">
                <slot :name="activeHash"></slot>
              </div>
            </transition>

            <slot name="after"></slot>
          </div>
        </div>
      </div>
    </transition>
  </portal>
</template>

<script>
import { mapState } from "vuex"

import { appHeight } from "@/lib/app-height-watcher"
import { isMobile } from "@/lib/is-platform"
import { lockScroll, unlockScroll } from "@/lib/scroll-lock"
import { toHashList } from "@/lib/url-helpers"
import { APP_HEIGHT_INITIAL_CSS_VAR } from "@shared/styles-utils"

export default {
  props: {
    vertical: { type: Boolean, default: false },
    persistent: { type: Boolean, default: false },
    short: { type: Boolean, default: false },
    xShort: { type: Boolean, default: false },
    fullHeight: { type: Boolean, default: false },
    disableClose: { type: Boolean, default: false },
    modalClass: { type: String, default: "" },
    right: { type: Boolean, default: false },
    overflowHidden: { type: Boolean, default: true }
  },

  data() {
    return {
      isOpen: false,
      isHidden: true,
      isHiding: false,
      pageContentHeight: 0,
      modalHashes: {},
      activeHash: "",
      appHeight,
      zIndex: 1,
      themeColorRestore: null,
      hideTimer: null,
      APP_HEIGHT_INITIAL_CSS_VAR
    }
  },

  created() {
    this.scrollLocked = false
  },

  mounted() {
    Object.keys(this.$slots).forEach(h => {
      if (h.startsWith("#")) {
        this.modalHashes[h.substring(1)] = true
      }
    })
  },

  computed: {
    ...mapState("videos", ["isFullscreen"]),
    ...mapState("responsive", ["isDesktop"]),

    transitionModalName() {
      return !this.vertical
        ? this.isDesktop
          ? "fade"
          : "fade-in-up-modal"
        : this.right
        ? "fade-from-right-modal"
        : "fade-from-left-modal"
    },

    appHeightStyle() {
      return `calc(${appHeight.height}px - ${this.pageContentHeight}px + 12px)`
    },

    isVisible() {
      return this.isOpen && !this.isHidden && !this.isHiding
    }
  },

  beforeDestroy() {
    clearTimeout(this.hideTimer)
    this.restoreThemeColor()
    this.setScrollLock(false)
  },

  methods: {
    closeModal() {
      if (!this.disableClose) {
        const hashList = toHashList(this.$route.hash)

        if (hashList.some(h => this.modalHashes[h])) {
          this.$router.removeHash()
        }

        if (this.persistent) {
          // Actually close the modal if the user explicitly closes it
          this.isOpen = false
        }

        this.$emit("close")
      }
    },

    setThemeColorBlack() {
      if (this.themeColorRestore) return

      let meta = document.querySelector('meta[name="theme-color"]')

      if (meta) {
        this.themeColorRestore = {
          meta,
          previous: meta.getAttribute("content")
        }
      } else {
        meta = document.createElement("meta")
        meta.setAttribute("name", "theme-color")
        document.head.appendChild(meta)
        this.themeColorRestore = { meta, previous: null }
      }

      meta.setAttribute("content", "#000000")
    },

    restoreThemeColor() {
      if (!this.themeColorRestore) return

      const { meta, previous } = this.themeColorRestore

      if (previous === null) {
        meta.parentNode && meta.parentNode.removeChild(meta)
      } else {
        meta.setAttribute("content", previous)
      }

      this.themeColorRestore = null
    },

    setScrollLock(locked) {
      if (locked === this.scrollLocked) return

      this.scrollLocked = locked

      if (locked) {
        lockScroll()
      } else {
        unlockScroll()
      }
    }
  },

  watch: {
    isVisible(visible) {
      this.setScrollLock(visible)
    },

    isFullscreen() {
      if (this.isFullscreen) {
        const modal = document.querySelector("[data-role=modal]")

        if (modal) modal.scrollTop = 0
      }
    },

    isOpen() {
      if (this.isOpen) {
        setTimeout(
          () => {
            const contentWrapper = document.querySelector(
              "[data-role=modal-content-wrapper]"
            )

            if (contentWrapper) {
              this.pageContentHeight = contentWrapper.getBoundingClientRect().height
            }

            if (this.isDesktop) {
              this.pageContentHeight += 120
            }
          },
          this.isDesktop ? 40 : 401
        ) // Animation time + 1ms
      }
    },

    "$route.hash": {
      immediate: true,
      handler: function(hashes, lastHash) {
        this.$nextTick(() => {
          const hashesList = toHashList(hashes)

          const index = hashesList.findIndex(v => this.modalHashes[v])
          const hashValue = hashesList[index]

          clearTimeout(this.hideTimer)

          if (hashValue) {
            const hash = `#${hashValue}`

            this.isOpen = true
            this.isHidden = false
            this.isHiding = false
            this.activeHash = hash
            this.$emit("open", hash)
            this.zIndex = 2 * index

            if (!lastHash) this.$emit("initialOpen", hash)

            if (this.fullHeight && isMobile()) {
              this.setThemeColorBlack()
            }
          } else {
            if (this.persistent) {
              this.isHiding = true

              this.hideTimer = setTimeout(() => {
                this.isHidden = true
                this.isHiding = false
              }, 360)
            } else {
              this.isOpen = false
              this.isHidden = true
              this.isHiding = false
            }

            this.restoreThemeColor()
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

$modal--x-short-height: 300px;
$modal--short-height: 475px;
$modal--space-top--desktop: 4rem;
$overlay--full-height: calc(var(--app-height) - 1vh - env(safe-area-inset-top));
$overlay--full-height--desktop: calc(
  var(--app-height) - ($modal--space-top--desktop * 2) -
    env(safe-area-inset-top)
);

.v-modal {
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  z-index: $modal--z-index;

  &.--hiding {
    transition: opacity 0.24s ease;
    transition-delay: 0.12s;
    opacity: 0;
  }

  &.--hidden {
    pointer-events: none;
    z-index: 0;
    opacity: 0;
    visibility: hidden;
  }

  &.--short {
    .v-modal__content {
      top: 40vh; // Fallback
      top: calc(100vh - #{$modal--short-height});
    }
  }

  &.--vertical {
    @include hide-scroll-bars;

    .v-modal__content {
      width: 400px;
      max-width: 80vw;
      min-height: 100%;
      top: 0;
      padding-bottom: 0;
      border-radius: 0;
    }

    .v-modal__close {
      padding-top: env(safe-area-inset-top);
      top: $size--4;
      right: -$size--10;

      .v-button {
        &:hover {
          background: rgba($color-rgb--white, 0.16);
        }
      }
    }

    &.--fixed-right {
      .v-modal__content {
        position: absolute;
        right: 0;
      }

      .v-modal__close {
        right: unset;
        left: -$size--10;
      }
    }
  }

  &.--x-short {
    .v-modal__content {
      top: 60vh; // Fallback
      top: calc(100vh - #{$modal--x-short-height});
    }
  }

  &.--full-height {
    padding-top: env(safe-area-inset-top);

    .v-modal__content {
      height: var(--app-height);
      overflow: hidden;
      top: 0;
      padding-bottom: 0;
    }

    .v-modal__content-wrapper {
      padding-bottom: 0;
    }
  }

  &.--video-fullscreen {
    overflow: hidden;

    .v-modal__content {
      top: 0;
      height: 100%;
      padding-bottom: 0;
    }

    .v-modal__backdrop {
      height: 100%;
    }
  }

  &.--w-overlay,
  &.--full-height {
    .v-modal__close {
      top: $size--4;
      right: $size--4;
    }
  }

  &.--w-overlay {
    .v-modal__content {
      top: calc(1vh + env(safe-area-inset-top));
      padding-bottom: 0;
      overflow: hidden;
      border-top-left-radius: $modal--border-radius;
      border-top-right-radius: $modal--border-radius;
      min-height: $overlay--full-height;
      max-height: $overlay--full-height;

      @media screen and (min-width: $breakpoint--xl) {
        top: 0 !important;

        min-height: $overlay--full-height--desktop;
        max-height: $overlay--full-height--desktop;
      }
    }

    .v-modal__content-wrapper {
      padding-top: $size--4;
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      background: $color-ui--white;
      border-top-left-radius: $modal--border-radius;
      border-top-right-radius: $modal--border-radius;
      box-shadow: $box-shadow--n-medium;
      z-index: 11;
    }

    .v-modal__close {
      .v-button {
        border-color: rgba($color-rgb--white, 0.24);
        background: rgba($color-rgb--black, 0.24);

        .v-icon {
          color: $color-ui--white;
        }

        &:hover {
          color: $color-ui--white;
          background: rgba($color-rgb--black, 0.5);
        }
      }
    }

    .v-modal__overlay {
      @include hide-scroll-bars;

      max-height: $overlay--full-height;
      overflow-y: scroll;
    }
  }

  &.--overflow-hidden {
    .v-modal__content-wrapper {
      overflow: hidden;
    }
  }

  @media screen and (min-width: $breakpoint--xl) {
    &:not(.--vertical) {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding: $modal--space-top--desktop 0;

      .v-modal__close {
        top: $size--1;
        right: $size--1;

        animation: fade-in 0.3s ease forwards;

        .v-button {
          border: none;

          .v-icon {
            color: $color-ui--grey-70;
          }

          &:hover {
            color: $color-ui--black;
            background: $color-ui--grey-15;
          }
        }
      }
    }
  }
}

.v-modal__backdrop {
  background: rgba(black, 0.6);
  width: 100%;
  height: 110%;
  top: -10%;
  left: 0;
  z-index: $modal--z-index - 1;

  opacity: 0;
  animation: fade-in 0.24s ease forwards;

  &.--full-height {
    top: 0 !important;
    background: black;

    @media screen and (max-width: $breakpoint--xl) {
      // Prevent flicker by rendering a smaller backdrop
      height: calc(10% + 50vh);
    }

    @media screen and (min-width: $breakpoint--xl) {
      background: rgba(black, 0.6);
    }
  }
}

.v-modal__content {
  z-index: 5;
  min-height: 100vh;
  top: $modal--offset-top;
  top: calc(#{$modal--offset-top} + env(safe-area-inset-top));
  padding-bottom: 40vh;
  border-top-left-radius: $modal--border-radius;
  border-top-right-radius: $modal--border-radius;
  background: $color-ui--white;
  transform: translate3d(0, 0, 0);

  @media screen and (min-width: $breakpoint--xl) {
    max-width: $content--max-width;
    width: $content--max-width;

    top: 0 !important;

    min-height: initial;
    border-radius: $modal--border-radius;

    margin: auto 0;

    padding-bottom: 0 !important;
  }
}

.v-modal__content-wrapper {
  padding-bottom: calc(#{$size--4} + env(safe-area-inset-bottom));
}

.v-modal__close {
  top: -$size--10;
  right: $size--4;
  opacity: 0;
  animation: fade-in 0.3s ease forwards;
  animation-delay: 0.5s;
  z-index: 100;

  .v-icon {
    color: $color-ui--white;
  }
}

[dir="rtl"] {
  .v-modal {
    &.--vertical {
      &.--fixed-right {
        .v-modal__content {
          right: unset;
          left: 0;
        }
      }
    }
  }
}
</style>
